import React, { useEffect, useMemo, useRef, useState } from "react";
import { mailService } from "../services/mailApi";
import "../styles/MailboxPanel.css";


function stripHtml(html) {
  return String(html || "")
    .replace(/<style[\s\S]*?<\/style>/gi, "")
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/<\/?[^>]+(>|$)/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function applyColorAtCursor(color, isHighlight = false) {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return;

  const range = sel.getRangeAt(0);

  // Seçili metin varsa → normal uygula
  if (!range.collapsed) {
    document.execCommand(isHighlight ? "hiliteColor" : "foreColor", false, color);
    return;
  }

  // Seçim yoksa → span aç
  const span = document.createElement("span");
  span.style[isHighlight ? "backgroundColor" : "color"] = color;
  span.appendChild(document.createTextNode("\u200B")); // zero-width space

  range.insertNode(span);

  // İmleci span içine al
  range.setStart(span.firstChild, 1);
  range.collapse(true);
  sel.removeAllRanges();
  sel.addRange(range);
}

function toDateSafe(v) {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

function isSameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function startOfWeekMonday(date) {
  // Pazartesi başlangıç
  const d = new Date(date);
  const day = d.getDay(); // 0 pazar
  const diff = (day === 0 ? -6 : 1) - day; // pazartesiye çek
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function getDateGroupLabel(dateObj) {
  const now = new Date();
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);

  const d = new Date(dateObj);
  const d0 = new Date(d);
  d0.setHours(0, 0, 0, 0);

  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  if (isSameDay(d0, today)) return "BUGÜN";
  if (isSameDay(d0, yesterday)) return "DÜN";

  const thisWeekStart = startOfWeekMonday(now);
  if (d0 >= thisWeekStart) return "BU HAFTA";

  return "DİĞER GÜNLER";
}

function formatListRightDate(dateObj) {
  const now = new Date();
  const today0 = new Date(now);
  today0.setHours(0, 0, 0, 0);

  const d0 = new Date(dateObj);
  const dDay = new Date(d0);
  dDay.setHours(0, 0, 0, 0);

  // Bugünse sadece saat
  if (dDay.getTime() === today0.getTime()) {
    return new Intl.DateTimeFormat("tr-TR", {
      hour: "2-digit",
      minute: "2-digit",
    }).format(d0);
  }

  const month = new Intl.DateTimeFormat("tr-TR", { month: "short" }).format(d0);
  const day = new Intl.DateTimeFormat("tr-TR", { day: "2-digit" }).format(d0);
  return `${month} ${day}`; 
}

function normalizeArray(data) {
  if (!data) return [];
  if (Array.isArray(data)) return data;

  if (Array.isArray(data.$values)) return data.$values;

  if (Array.isArray(data.items)) return data.items;

  if (data.items && Array.isArray(data.items.$values)) return data.items.$values;

  if (data.messages) return normalizeArray(data.messages);
  if (data.data) return normalizeArray(data.data);
  if (data.result) return normalizeArray(data.result);

  return [];
}

function normalizeContacts(data) {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.$values)) return data.$values;
  return [];
}

function getFolderIcon(name) {
  const n = String(name || "").toUpperCase();

  if (n === "INBOX") return "📥";
  if (n.includes("SENT")) return "📤";
  if (n.includes("TRASH") || n.includes("DELETED")) return "🗑️";
  if (n.includes("ARCHIVE")) return "🗄️";
  return "📁";
}

function getFolderNameTR(name) {
  const n = String(name || "").toUpperCase();

  if (n === "INBOX") return "Gelen Kutusu";
  if (n.includes("SENT")) return "Gönderilenler";
  if (n.includes("TRASH") || n.includes("DELETED")) return "Çöp Kutusu";
  if (n.includes("ARCHIVE")) return "Arşiv";

  return name; // bilinmeyen klasörler aynen kalsın
}

/** React içinde obje basmayı engeller */
function asText(v) {
  if (v === null || v === undefined) return "";
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);

  if (typeof v === "object") {
    if (v.address) return v.name ? `${v.name} <${v.address}>` : String(v.address);
    if (v.email) return v.name ? `${v.name} <${v.email}>` : String(v.email);
    if (v.fullName) return String(v.fullName);
    if (v.name) return String(v.name);

    try {
      return JSON.stringify(v);
    } catch {
      return String(v);
    }
  }
  return String(v);
}

function formatFrom(obj) {
  const name = asText(obj?.fromName);
  const email = asText(obj?.fromEmail);

  if (name && email) return `${name} <${email}>`;
  if (name) return name;
  if (email) return email;

  return asText(obj?.from ?? obj?.sender ?? obj?.fromAddress ?? obj?.fromEmail ?? "");
}


function pickId(m, idx) {
  const raw = m?.uid ?? m?.id ?? m?.messageId ?? m?.uidValidity;
  return raw ?? `row-${idx}`;
}

const MAIL_FOLDERS = {
  INBOX: "INBOX",
  JUNK: "Junk",
  DRAFTS: "Drafts",
  SENT: "Sent",
  ARCHIVE: "Archive",
  TRASH: "Trash",
};

export default function MailboxPanel({ sessionId, userEmail }) {


  const isSelected = (uid) => selectedUids.includes(uid);

  const toggleSelectUid = (uid) => {
    setSelectedUids((prev) => (prev.includes(uid) ? prev.filter((x) => x !== uid) : [...prev, uid]));
  };

  const clearSelection = () => setSelectedUids([]);

  const selectAllVisible = () => {
    const all = messages.map((m) => (m.uid ?? m.id)).filter((x) => x !== null && x !== undefined);
    setSelectedUids(all);
  };

  const bulkMoveTo = async (toFolder) => {
    if (authErr) return;
    if (!selectedUids.length) return;

    try {
      await Promise.all(
        selectedUids.map((uid) =>
          mailService.moveMessage({
            uid,
            sessionId,
            fromFolder: activeFolder,
            toFolder,
          })
        )
      );

      // UI: listeden kaldır
      setMessages((prev) => prev.filter((m) => !selectedUids.includes(m.uid ?? m.id)));

      // detail seçiliyse kapat
      if (selectedUid && selectedUids.includes(selectedUid)) {
        setSelectedUid(null);
        setDetail(null);
      }

      clearSelection();
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) setAuthErr("Mail oturumu yenilendi/bozuldu. Lütfen tekrar giriş yap veya sayfayı yenile.");
      else console.error("Bulk move error:", e);
    }
  };


  const [selectedUids, setSelectedUids] = useState([]);

  const resetCompose = () => {
    setCompose({ to: "", cc: "", bcc: "", subject: "", bodyText: "", bodyHtml: "" });

    if (editorRef?.current) editorRef.current.innerHTML = "";

    setContactResults([]);
    setShowContacts(false);
    setContactOpenFor(null);
  };

  const editorRef = useRef(null);

  const execCmd = (command, value = null) => {
    if (editorRef.current) editorRef.current.focus();

    document.execCommand(command, false, value);

    const html = editorRef.current?.innerHTML || "";
    const text = editorRef.current?.innerText || "";

    setCompose((p) => ({
      ...p,
      bodyHtml: html,
      bodyText: text,
    }));
  };

  const [folders, setFolders] = useState([]);
  const [activeFolder, setActiveFolder] = useState(MAIL_FOLDERS.INBOX);

  const [messages, setMessages] = useState([]);
  const [loadingList, setLoadingList] = useState(false);

  const [selectedUid, setSelectedUid] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const [beforeUid, setBeforeUid] = useState(null);
  const [hasMore, setHasMore] = useState(true);

  const [foldersCollapsed, setFoldersCollapsed] = useState(true);

  const [contacts, setContacts] = useState([]);
  const [contactQuery, setContactQuery] = useState("");
  const [contactResults, setContactResults] = useState([]);
  const [showContacts, setShowContacts] = useState(false);
  const [contactOpenFor, setContactOpenFor] = useState(null); // "to" | "cc" | "bcc"
  const contactTimer = useRef(null);

  const [showCompose, setShowCompose] = useState(false);
  const [compose, setCompose] = useState({
    to: "",
    cc: "",
    bcc: "",
    subject: "",
    bodyHtml: "",
  });

  const getSentFolderId = () => {
    const f = safeFolders.find(x => {
      const a = String(x.id || "").toUpperCase();
      const b = String(x.fullName || "").toUpperCase();
      const c = String(x.name || "").toUpperCase();
      return a.includes("SENT") || b.includes("SENT") || c.includes("SENT") || c.includes("GİDEN") || c.includes("GIDEN");
    });

    return f?.id || MAIL_FOLDERS.SENT;
  };


  const [authErr, setAuthErr] = useState("");
  const [mailReady, setMailReady] = useState(false);
  const sessionVersionRef = useRef(0);

  const safeFolders = useMemo(() => {
    const list = folders?.length
      ? folders
      : [{ name: MAIL_FOLDERS.INBOX, fullName: MAIL_FOLDERS.INBOX }];

    return list
      .filter((f) => {
        const folderName = (f.fullName || f.name || "").toUpperCase();
        return folderName !== "DRAFTS" && folderName !== "JUNK";
      })
      .map((f, idx) => {
        if (typeof f === "string") {
          return { id: f, name: f, fullName: f, _key: `f-${f}-${idx}` };
        }

        const id = f.fullName ?? f.id ?? f.name ?? `folder-${idx}`;
        const name = f.name ?? f.fullName ?? f.id ?? String(id);

        return {
          ...f,
          id,
          name,
          fullName: f.fullName ?? id,
          _key: `f-${id}-${idx}`,
        };
      });
  }, [folders]);


  useEffect(() => {
    if (!sessionId) {
      setMailReady(false);
      return;
    }

    setAuthErr("");
    setMessages([]);
    setBeforeUid(null);
    setSelectedUid(null);
    setDetail(null);
    setHasMore(true);

    sessionVersionRef.current += 1;

    setMailReady(false);
    const t = setTimeout(() => setMailReady(true), 0);
    return () => clearTimeout(t);
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) return;

    (async () => {
      try {
        const data = await mailService.getFolders(sessionId);
        const list = normalizeArray(data);

        const normalized = list.map((x) => {
          if (typeof x === "string") return { name: x, fullName: x };
          return {
            name: x.name ?? x.fullName ?? "Folder",
            fullName: x.fullName ?? x.name,
          };
        });

        setFolders(normalized);

        const inbox = normalized.find((f) => (f.fullName || "").toUpperCase() === "INBOX");
        if (inbox) setActiveFolder(inbox.fullName);
      } catch (e) {
        console.error("Folders error:", e);
        setFolders([
          { name: MAIL_FOLDERS.INBOX, fullName: MAIL_FOLDERS.INBOX },
          { name: MAIL_FOLDERS.SENT, fullName: MAIL_FOLDERS.SENT },
          { name: MAIL_FOLDERS.TRASH, fullName: MAIL_FOLDERS.TRASH },
        ]);
      }
    })();
  }, [sessionId]);

  const selectContact = (c) => {
    const value = `${c.displayName} <${c.email}>`;

    setCompose((prev) => {
      const field = contactOpenFor;
      if (!field) return prev;

      const current = String(prev[field] || "");
      const parts = current.split(",");
      parts[parts.length - 1] = ` ${value}`;

      const next = parts
        .map((x) => x.trim())
        .filter(Boolean)
        .join(", ");

      return {
        ...prev,
        [field]: next ? next + ", " : value + ", ",
      };
    });

    setContactResults([]);
    setShowContacts(false);
    setContactOpenFor(null);
  };


  const loadMessages = async ({ reset = false, folderOverride } = {}) => {
    if (!sessionId) return;
    if (loadingList) return;
    if (authErr) return;

    const localVersion = sessionVersionRef.current;

    setLoadingList(true);
    try {
      const take = 10;
      const folderToUse = folderOverride || activeFolder;

      const data = await mailService.getMessages({
        sessionId,
        folder: folderToUse,
        take,
        beforeUid: reset ? undefined : beforeUid,
      });

      if (localVersion !== sessionVersionRef.current) return;

      const list = normalizeArray(data);

      const apiCursor = data?.nextBeforeUid ?? data?.beforeUid ?? data?.nextCursor ?? null;

      const lastRowId = list.length ? (list[list.length - 1]?.uid ?? list[list.length - 1]?.id) : null;
      const nextBefore = apiCursor ?? lastRowId ?? null;

      setMessages((prev) => (reset ? list : [...prev, ...list]));
      setBeforeUid(nextBefore);

      setHasMore(list.length === take);
    } catch (e) {
      const status = e?.response?.status;

      if (status === 401) {
        setAuthErr("Mail oturumu yenilendi/bozuldu. Lütfen tekrar giriş yap veya sayfayı yenile.");
        setMessages([]);
        setHasMore(false);
        setBeforeUid(null);
        setSelectedUid(null);
        setDetail(null);
        return;
      }

      console.error("Messages error:", e);
    } finally {
      if (localVersion === sessionVersionRef.current) setLoadingList(false);
    }
  };

  const searchContacts = (q, field) => {
    clearTimeout(contactTimer.current);

    setContactOpenFor(field);

    const query = String(q || "").trim();
    setContactQuery(query);

    if (!query) {
      setContactResults([]);
      return;
    }

    contactTimer.current = setTimeout(async () => {
      try {
        const data = await mailService.getContacts({ sessionId, q: query, take: 10 });
        const list = normalizeContacts(data); // ✅ contacts için
        setContactResults(list);
        setShowContacts(true);
      } catch (e) {
        console.error("Contact search error", e);
        setContactResults([]);
        setShowContacts(false);
      }
    }, 250);
  };

  useEffect(() => {
    if (!sessionId) return;
    if (!mailReady) return;
    if (authErr) return;

    setActiveFolder(MAIL_FOLDERS.INBOX);
    loadMessages({ reset: true, folderOverride: MAIL_FOLDERS.INBOX });
  }, [sessionId, mailReady]);

  const handleSelectFolder = (folderId) => {
    if (!folderId) return;
    if (authErr) return;

    setActiveFolder(folderId);

    // UI temiz
    setMessages([]);
    setBeforeUid(null);
    setSelectedUid(null);
    setDetail(null);
    setHasMore(true);

    // yeni folder mesajları
    loadMessages({ reset: true, folderOverride: folderId });
  };

  const toggleFolders = () => setFoldersCollapsed((prev) => !prev);

  const openMessage = async (uid) => {
    if (uid === null || uid === undefined) return;
    if (!sessionId) return;
    if (authErr) return;

    setSelectedUid(uid);
    setLoadingDetail(true);

    try {
      const data = await mailService.getMessageDetail({
        sessionId,
        folder: activeFolder,
        uid,
      });

      setDetail(data);

      // okundu flag
      try {
        await mailService.setFlags({ uid, sessionId, folder: activeFolder, isRead: true });
        setMessages((prev) => prev.map((m) => ((m.uid ?? m.id) === uid ? { ...m, isRead: true } : m)));
      } catch (_) { }
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) {
        setAuthErr("Mail oturumu yenilendi/bozuldu. Lütfen tekrar giriş yap veya sayfayı yenile.");
        setDetail(null);
        setSelectedUid(null);
      } else {
        console.error("Detail error:", e);
      }
    } finally {
      setLoadingDetail(false);
    }
  };

  const toggleRead = async (uid, isReadNow) => {
    if (authErr) return;
    try {
      await mailService.setFlags({ uid, sessionId, folder: activeFolder, isRead: !isReadNow });
      setMessages((prev) => prev.map((m) => ((m.uid ?? m.id) === uid ? { ...m, isRead: !isReadNow } : m)));
      if (selectedUid === uid && detail) setDetail({ ...detail, isRead: !isReadNow });
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) setAuthErr("Mail oturumu yenilendi/bozuldu. Lütfen tekrar giriş yap veya sayfayı yenile.");
      else console.error("Flag error:", e);
    }
  };

  const moveTo = async (uid, toFolder) => {
    if (authErr) return;
    try {
      await mailService.moveMessage({ uid, sessionId, fromFolder: activeFolder, toFolder });
      setMessages((prev) => prev.filter((m) => (m.uid ?? m.id) !== uid));
      if (selectedUid === uid) {
        setSelectedUid(null);
        setDetail(null);
      }
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) setAuthErr("Mail oturumu yenilendi/bozuldu. Lütfen tekrar giriş yap veya sayfayı yenile.");
      else console.error("Move error:", e);
    }
  };

  const send = async () => {
    if (authErr) return;

    const toArr = compose.to.split(",").map((x) => x.trim()).filter(Boolean);
    const ccArr = compose.cc.split(",").map((x) => x.trim()).filter(Boolean);
    const bccArr = compose.bcc.split(",").map((x) => x.trim()).filter(Boolean);

    try {
      const html = compose.bodyHtml || "";
      const text = compose.bodyText || stripHtml(html);

      await mailService.sendMail({
        sessionId,
        fromEmail: userEmail,
        to: toArr,
        cc: ccArr,
        bcc: bccArr,
        subject: compose.subject,
        bodyText: text,
        bodyHtml: html,
      });

      setShowCompose(false);
      setCompose({
        to: "",
        cc: "",
        bcc: "",
        subject: "",
        bodyText: "",
        bodyHtml: "",
      });

      const sentFolder = getSentFolderId();

      setActiveFolder(sentFolder);
      setMessages([]);
      setBeforeUid(null);
      setSelectedUid(null);
      setDetail(null);
      setHasMore(true);

      loadMessages({ reset: true, folderOverride: sentFolder });

    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) {
        setAuthErr("Mail oturumu yenilendi/bozuldu. Lütfen tekrar giriş yap veya sayfayı yenile.");
      } else {
        console.error("Send error:", e);
      }
    }

  };
  const sortedMessages = useMemo(() => {
    const arr = Array.isArray(messages) ? [...messages] : [];

    arr.sort((a, b) => {
      const da = new Date(a?.date ?? a?.receivedAt ?? a?.internalDate ?? 0);
      const db = new Date(b?.date ?? b?.receivedAt ?? b?.internalDate ?? 0);

      const ta = isNaN(da) ? 0 : da.getTime();
      const tb = isNaN(db) ? 0 : db.getTime();

      if (tb !== ta) return tb - ta;

      return (b?.uid ?? 0) - (a?.uid ?? 0);
    });

    return arr;
  }, [messages]);
  const isRestoreFolder =
    activeFolder === MAIL_FOLDERS.ARCHIVE ||
    activeFolder === MAIL_FOLDERS.TRASH;

  const restoreTargetFolder = MAIL_FOLDERS.INBOX;


  if (!sessionId) {
    return (
      <div className="fulla-mailbox">
        <div className="fulla-mailbox-empty">
          Mail sessionId yok. Login response’tan mailbox.sessionId’yi buraya geçirmen lazım.
        </div>
      </div>
    );
  }

  return (
    <div className="fulla-mailbox">
      <div className="fulla-mailbox-top">
        <div className="fulla-mailbox-title"></div>
        <div className="fulla-mailbox-actions">
          <button className="fulla-btn" onClick={() => setShowCompose(true)} disabled={!!authErr}>
            Yeni Mail
          </button>
          <button className="fulla-btn-outline" onClick={() => loadMessages({ reset: true })} disabled={!!authErr}>
            Yenile
          </button>
        </div>
      </div>

      <div className="fulla-mailbox-body">
        <aside className={`fulla-mailbox-folders ${foldersCollapsed ? "collapsed" : ""}`}>
          <div className="fulla-folders-head">
            <div className="fulla-mailbox-section-title">Klasörler</div>

            <button
              type="button"
              className="fulla-folders-toggle-inpanel"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                toggleFolders();
              }}
              aria-label={foldersCollapsed ? "Klasörleri aç" : "Klasörleri kapat"}
              title={foldersCollapsed ? "Klasörleri aç" : "Klasörleri kapat"}
            >
              {foldersCollapsed ? "›" : "‹"}
            </button>
          </div>

          <div className="fulla-folders-list">
            {safeFolders.map((f) => {
              const icon = getFolderIcon(f.id || f.fullName || f.name);

              return (
                <button
                  type="button"
                  key={f._key}
                  className={`fulla-folder ${activeFolder === f.id ? "active" : ""}`}
                  onClick={() => handleSelectFolder(f.id)}
                  disabled={!!authErr}
                  data-tip={f.name}
                  title={f.name}
                >
                  <span className="fulla-folder-icon">{icon}</span>
                  <span className="fulla-folder-label">
                    {getFolderNameTR(f.name)}
                  </span>
              </button>
              );
            })}
          </div>
        </aside>

        <section className="fulla-mailbox-list">
          <div className="fulla-mailbox-section-title fulla-list-head">
            <span>{activeFolder}</span>

            {selectedUids.length > 0 && (
              <div className="fulla-bulkbar" onClick={(e) => e.stopPropagation()}>
                <span className="fulla-bulkcount">{selectedUids.length} seçili</span>

                <button
                  className="fulla-btn-outline"
                  type="button"
                  onClick={selectAllVisible}
                  disabled={!!authErr}
                >
                  Tümünü Seç
                </button>

                <button
                  className="fulla-btn-outline"
                  type="button"
                  onClick={clearSelection}
                  disabled={!!authErr}
                >
                  Seçimi Kaldır
                </button>

                {isRestoreFolder ? (
                  <>
                    <button
                      className="fulla-btn-outline"
                      type="button"
                      onClick={() => bulkMoveTo(restoreTargetFolder)}
                      disabled={!!authErr}
                    >
                      Inbox’a Taşı
                    </button>

                    {/* TRASH içindeyken “Sil” butonunu istersen gizle, çünkü zaten çöp */}
                    {activeFolder !== MAIL_FOLDERS.TRASH && (
                      <button
                        className="fulla-btn"
                        type="button"
                        onClick={() => bulkMoveTo(MAIL_FOLDERS.TRASH)}
                        disabled={!!authErr}
                      >
                        Sil
                      </button>
                    )}
                  </>
                ) : (
                  <>
                    <button
                      className="fulla-btn-outline"
                      type="button"
                      onClick={() => bulkMoveTo(MAIL_FOLDERS.ARCHIVE)}
                      disabled={!!authErr}
                    >
                      Arşivle
                    </button>

                    <button
                      className="fulla-btn"
                      type="button"
                      onClick={() => bulkMoveTo(MAIL_FOLDERS.TRASH)}
                      disabled={!!authErr}
                    >
                      Sil
                    </button>
                  </>
                )}
              </div>
            )}
          </div>

          {authErr ? <div className="fulla-mailbox-empty">{authErr}</div> : null}

          {loadingList && messages.length === 0 ? <div className="fulla-loading">Yükleniyor...</div> : null}

          {!authErr && (() => {
            let lastGroup = null;

            return sortedMessages.map((m, idx) => {
              const uid = m.uid ?? m.id;
              const key = pickId(m, idx);

              const dateRaw = m.date ?? m.receivedAt ?? m.internalDate ?? "";
              const dateObj = toDateSafe(dateRaw);
              const groupLabel = dateObj ? getDateGroupLabel(dateObj) : "DİĞER GÜNLER";
              const rightDateText = dateObj ? formatListRightDate(dateObj) : asText(dateRaw);

              const from = asText(
                m.from ??
                m.sender ??
                m.fromAddress ??
                (m.fromName ? `${m.fromName}${m.fromEmail ? ` <${m.fromEmail}>` : ""}` : m.fromEmail)
              );

              const subject = asText(m.subject ?? "(Konu yok)");
              const snippet = asText(m.preview ?? m.snippet ?? m.bodyPreview ?? "");
              const isRead = !!m.isRead;

              const showHeader = groupLabel !== lastGroup;
              lastGroup = groupLabel;

              return (
                <React.Fragment key={key}>
                  {showHeader && (
                    <div className="fulla-mail-group-head">
                      {groupLabel}
                    </div>
                  )}

                  <div
                    className={`fulla-mail-row ${selectedUid === uid ? "selected" : ""} ${isRead ? "read" : "unread"}`}
                    onClick={() => openMessage(uid)}
                  >
                    {/* SOL */}
                    <div className="fulla-mail-left" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        className="fulla-mail-check"
                        checked={isSelected(uid)}
                        onChange={() => toggleSelectUid(uid)}
                      />
                      <button className="fulla-mail-star" type="button" title="Yıldızla">
                        ☆
                      </button>
                      <span className={`fulla-mail-dot ${isRead ? "" : "on"}`} />
                    </div>

                    {/* ORTA */}
                    <div className="fulla-mail-mid">
                      <div className="fulla-mail-topline">
                        <div className="fulla-mail-from2">{from || "(Gönderen yok)"}</div>
                        <div className="fulla-mail-date2">{rightDateText}</div>
                      </div>

                      <div className="fulla-mail-subline">
                        <span className="fulla-mail-subject2">{subject}</span>
                        {snippet ? <span className="fulla-mail-snippet"> — {snippet}</span> : null}
                      </div>
                    </div>

                    {/* SAĞ */}
                    <div className="fulla-mail-right" onClick={(e) => e.stopPropagation()}>
                      {!isRestoreFolder ? (
                        <>
                          <button
                            className="fulla-icon-btn"
                            title={isRead ? "Okunmadı yap" : "Okundu yap"}
                            onClick={() => toggleRead(uid, isRead)}
                          >
                            {isRead ? "✉️" : "📩"}
                          </button>

                          <button
                            className="fulla-icon-btn"
                            title="Arşivle"
                            onClick={() => moveTo(uid, MAIL_FOLDERS.ARCHIVE)}
                          >
                            🗄️
                          </button>

                          <button
                            className="fulla-icon-btn danger"
                            title="Çöpe taşı"
                            onClick={() => moveTo(uid, MAIL_FOLDERS.TRASH)}
                          >
                            🗑️
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            className="fulla-icon-btn"
                            title="Inbox’a taşı"
                            onClick={() => moveTo(uid, restoreTargetFolder)}
                          >
                            ↩️
                          </button>

                          {activeFolder !== MAIL_FOLDERS.TRASH && (
                            <button
                              className="fulla-icon-btn danger"
                              title="Çöpe taşı"
                              onClick={() => moveTo(uid, MAIL_FOLDERS.TRASH)}
                            >
                              🗑️
                            </button>
                          )}
                        </>
                      )}
                    </div>

                  </div>
                </React.Fragment>
              );
            });
          })()}


          <div className="fulla-mailbox-list-footer">
            {authErr ? null : hasMore ? (
              <button className="fulla-btn-outline" disabled={loadingList} onClick={() => loadMessages()}>
                {loadingList ? "Yükleniyor..." : "Daha Fazla"}
              </button>
            ) : messages.length === 0 ? (
              <div className="fulla-muted">Inbox boş.</div>
            ) : (
              <div className="fulla-muted">Daha fazla mesaj yok.</div>
            )}
          </div>
        </section>

        <section className="fulla-mailbox-detail">
          <div className="fulla-mailbox-section-title">Detay</div>

          {loadingDetail ? <div className="fulla-loading">Açılıyor...</div> : null}

          {!detail ? (
            <div className="fulla-muted">{authErr ? "Oturum hatası var." : "Bir mesaj seç."}</div>
          ) : (
            <>
              <div className="fulla-detail-head">
                <div className="fulla-detail-subject">{asText(detail.subject || "(Konu yok)")}</div>
                <div className="fulla-detail-meta">
                  <div>
                    <b>Kimden:</b> {formatFrom(detail) || "(Gönderen yok)"}
                  </div>
                  <div>
                    <b>Tarih:</b> {asText(detail.date)}
                  </div>
                </div>

                <div className="fulla-detail-actions">
                  <button
                    className="fulla-btn-outline"
                    onClick={() => moveTo(selectedUid, MAIL_FOLDERS.ARCHIVE)}
                    disabled={!!authErr}
                  >
                    Arşiv
                  </button>
                  <button className="fulla-btn" onClick={() => moveTo(selectedUid, MAIL_FOLDERS.TRASH)} disabled={!!authErr}>
                    Çöp
                  </button>
                </div>
              </div>

              <div className="fulla-detail-body">
                {detail.bodyHtml ? (
                  <div className="fulla-html" dangerouslySetInnerHTML={{ __html: String(detail.bodyHtml) }} />
                ) : (
                  <pre className="fulla-text">{asText(detail.bodyText || "")}</pre>
                )}
              </div>

              {Array.isArray(detail.attachments) && detail.attachments.length > 0 ? (
                <div className="fulla-attachments">
                  <div className="fulla-mailbox-section-title">Ekler</div>

                  {detail.attachments.map((a, i) => {
                    const attachmentId = a?.attachmentId || a?.id || `att-${i}`;
                    const url = mailService.downloadAttachmentUrl({
                      uid: selectedUid,
                      attachmentId,
                      sessionId,
                      folder: activeFolder,
                    });

                    return (
                      <a key={attachmentId} className="fulla-attachment" href={url} target="_blank" rel="noreferrer">
                        {asText(a?.name || a?.fileName || "Ek")}
                      </a>
                    );
                  })}
                </div>
              ) : null}
            </>
          )}
        </section>
      </div>

      {showCompose && (
        <div
          className="fulla-compose-overlay"
          onClick={() => {
            resetCompose();
            setShowCompose(false);
          }}
        >
          <div className="fulla-compose" onClick={(e) => e.stopPropagation()}>
            <div className="fulla-compose-top">
              <div className="fulla-mailbox-title">Yeni Mail</div>
              <button className="fulla-x" onClick={() => { resetCompose(); setShowCompose(false); }}>
                ✖
              </button>
            </div>
            <div className="fulla-compose-row" style={{ position: "relative" }}>
              <label>To</label>
              <input
                value={compose.to}
                onChange={(e) => {
                  const val = e.target.value;
                  setCompose({ ...compose, to: val });

                  const last = val.split(",").pop().trim();
                  searchContacts(last, "to");
                }}
                placeholder="Bir isim veya mail yaz..."
                onFocus={() => {
                  const last = compose.to.split(",").pop().trim();
                  if (last) searchContacts(last, "to");
                }}
                onBlur={() => setTimeout(() => setShowContacts(false), 150)}
              />

              {showContacts && contactOpenFor === "to" && contactResults.length > 0 && (
                <div className="fulla-contact-dropdown">
                  {contactResults.map((c) => (
                    <div key={c.userId} className="fulla-contact-item" onMouseDown={() => selectContact(c)}>
                      <div className="name">{c.displayName}</div>
                      <div className="email">{c.email}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="fulla-compose-row" style={{ position: "relative" }}>
              <label>Cc</label>
              <input
                value={compose.cc}
                onChange={(e) => {
                  const val = e.target.value;
                  setCompose({ ...compose, cc: val });

                  const last = val.split(",").pop().trim();
                  searchContacts(last, "cc");
                }}
                onFocus={() => {
                  const last = compose.cc.split(",").pop().trim();
                  if (last) searchContacts(last, "cc");
                }}
                onBlur={() => setTimeout(() => setShowContacts(false), 150)}
              />

              {showContacts && contactOpenFor === "cc" && contactResults.length > 0 && (
                <div className="fulla-contact-dropdown">
                  {contactResults.map((c) => (
                    <div key={c.userId} className="fulla-contact-item" onMouseDown={() => selectContact(c)}>
                      <div className="name">{c.displayName}</div>
                      <div className="email">{c.email}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="fulla-compose-row" style={{ position: "relative" }}>
              <label>Bcc</label>
              <input
                value={compose.bcc}
                onChange={(e) => {
                  const val = e.target.value;
                  setCompose({ ...compose, bcc: val });

                  const last = val.split(",").pop().trim();
                  searchContacts(last, "bcc");
                }}
                onFocus={() => {
                  const last = compose.bcc.split(",").pop().trim();
                  if (last) searchContacts(last, "bcc");
                }}
                onBlur={() => setTimeout(() => setShowContacts(false), 150)}
              />

              {showContacts && contactOpenFor === "bcc" && contactResults.length > 0 && (
                <div className="fulla-contact-dropdown">
                  {contactResults.map((c) => (
                    <div key={c.userId} className="fulla-contact-item" onMouseDown={() => selectContact(c)}>
                      <div className="name">{c.displayName}</div>
                      <div className="email">{c.email}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="fulla-compose-row">
              <label>Konu</label>
              <input value={compose.subject} onChange={(e) => setCompose({ ...compose, subject: e.target.value })} />
            </div>

            <div className="fulla-compose-row">
              <label>Mesaj</label>

              {/* Toolbar */}
              <div className="fulla-editor-toolbar">

                <span className="fulla-ed-sep" />

                <select
                  className="fulla-ed-select"
                  defaultValue="Arial"
                  onChange={(e) => execCmd("fontName", e.target.value)}
                  title="Font"
                >
                  <option value="Arial">Arial</option>
                  <option value="Helvetica">Helvetica</option>
                  <option value="Verdana">Verdana</option>
                  <option value="Tahoma">Tahoma</option>
                  <option value="Georgia">Georgia</option>
                  <option value="Times New Roman">Times New Roman</option>
                  <option value="Courier New">Courier New</option>

                </select>

                <select
                  className="fulla-ed-select"
                  defaultValue="3"
                  onChange={(e) => execCmd("fontSize", e.target.value)}
                  title="Boyut"
                >
                  <option value="2">Küçük</option>
                  <option value="3">Normal</option>
                  <option value="4">Büyük</option>
                  <option value="5">Çok büyük</option>
                </select>

                <span className="fulla-ed-sep" />

                <button
                  type="button"
                  className="fulla-ed-btn"
                  title="Kalın"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => execCmd("bold")}
                >
                  <b>B</b>
                </button>

                <button
                  type="button"
                  className="fulla-ed-btn"
                  title="İtalik"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => execCmd("italic")}
                >
                  <i>I</i>
                </button>

                <button
                  type="button"
                  className="fulla-ed-btn"
                  title="Altı çizili"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => execCmd("underline")}
                >
                  <u>U</u>
                </button>

                <span className="fulla-ed-sep" />

                <label className="fulla-ed-color" title="Yazı rengi">
                  A
                  <input
                    type="color"
                    onChange={(e) => applyColorAtCursor(e.target.value)}
                  />
                </label>

                <label className="fulla-ed-color" title="Highlight">
                  🖍
                  <input
                    type="color"
                    onMouseDown={(e) => e.stopPropagation()}
                    onChange={(e) => applyColorAtCursor(e.target.value, true)}
                  />
                </label>

                <span className="fulla-ed-sep" />

                <button
                  type="button"
                  className="fulla-ed-btn"
                  title="Resim ekle (URL)"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    const url = prompt("Resim URL gir:");
                    if (url) execCmd("insertImage", url);
                  }}
                >
                  🖼️
                </button>

                <button
                  type="button"
                  className="fulla-ed-btn"
                  title="Link ekle"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    const url = prompt("Link URL gir:");
                    if (url) execCmd("createLink", url);
                  }}
                >
                  🔗
                </button>

                <button
                  type="button"
                  className="fulla-ed-btn"
                  title="Link kaldır"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => execCmd("unlink")}
                >
                  ⛓️‍💥
                </button>

                <button
                  type="button"
                  className="fulla-ed-btn"
                  title="Temizle"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    if (editorRef.current) {
                      editorRef.current.innerHTML = "";
                    }
                    setCompose((p) => ({ ...p, bodyHtml: "", bodyText: "" }));
                  }}
                >
                  🧹
                </button>
              </div>


              <div
                ref={editorRef}
                className="fulla-editor"
                contentEditable
                suppressContentEditableWarning
                onInput={() => {
                  const html = editorRef.current?.innerHTML || "";
                  const text = editorRef.current?.innerText || "";

                  setCompose((p) => ({
                    ...p,
                    bodyHtml: html,
                    bodyText: text,
                  }));
                }}
              />

            </div>


            <div className="fulla-compose-actions">
              <button className="fulla-btn" onClick={send} disabled={!!authErr}>
                Gönder
              </button>
              <button className="fulla-btn-outline" onClick={() => { resetCompose(); setShowCompose(false); }}
              >
                Vazgeç
              </button>

            </div>
          </div>
        </div>
      )}
    </div>
  );
}
